import type { CustomSEO, SEOPageType } from '@iheartjane/data-access';
import { Button, Flex, Typography } from '@iheartjane/reefer';
import { Form } from '@iheartjane/reefer-hook-form';
import { useCallback, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { useSeoForm } from '../providers/SeoFormProvider';
import { SEO_URLS, SEO_VARIABLES } from './defaultSeoConstants';
import PreviewSeo from './PreviewSeo';

interface PageSeoProps {
  menuUrl: string;
  pageDescription: string;
  pageType: SEOPageType;
  seoVariables: Record<string, string | undefined>;
}

const MALFORMED_BRACE_PATTERNS = [
  /\{\{\s*\{/g, // {{ {
  /\{\{\s*\{\{/g, // {{ {{
  /\}\s*\}\}/g, // } }}
  /\}\}\s*\}\}/g, // }} }}
];
const VARIABLE_REGEX = /\{\{\s*([^}]+?)\s*\}\}/g;

export const PageSeo = ({
  menuUrl,
  pageDescription,
  pageType,
  seoVariables,
}: PageSeoProps) => {
  const { setupEventListeners, insertVariable } = useSeoForm();
  const { control } = useFormContext<CustomSEO>();
  const formValues = useWatch({ control });
  const cursorTrackingFields = [`${pageType}.title`, `${pageType}.description`];
  const pageVariables = SEO_VARIABLES[pageType];

  useEffect(() => {
    return setupEventListeners(cursorTrackingFields);
  }, [cursorTrackingFields, setupEventListeners]);

  const validateField = useCallback(
    (value: string): string | true => {
      const stringValue = typeof value === 'string' ? value : '';

      if (!stringValue) {
        return true;
      }

      for (const pattern of MALFORMED_BRACE_PATTERNS) {
        if (pattern.test(stringValue)) {
          const errorMessage =
            'Invalid curly braces. Use double curly braces like {{ variableName }}';
          return errorMessage;
        }
      }

      const variableMatches = [...stringValue.matchAll(VARIABLE_REGEX)];
      const invalidVariables = variableMatches
        .map((match) => match[1].trim())
        .filter((variable) => !pageVariables.includes(variable));

      if (invalidVariables.length > 0) {
        const errorMessage = `"${invalidVariables[0]}" is not a valid variable`;
        return errorMessage;
      }

      return true;
    },
    [pageVariables, pageType]
  );

  return (
    <Flex flexDirection="column">
      <Typography>
        Creating descriptive titles and descriptions for {pageDescription} (
        <span
          style={{
            whiteSpace: 'pre',
            fontFamily: 'monospace',
            padding: '2px 4px',
          }}
        >
          {SEO_URLS[pageType]}
        </span>
        ) is crucial for search engines. Use the listed variables to create a
        template for pages. Some variables will use dummy sample data and the
        default shows what Jane populates currently. Jane's default page titles
        and descriptions are optimized to maximize store traffic from Google.
        Partners customizing their page details may experience decreased search
        traffic.
      </Typography>
      <Flex flexDirection="column">
        <Form.TextField
          name={`${pageType}.title`}
          label="Page title"
          my={32}
          required
          validate={validateField}
        />
        <Form.TextAreaField
          name={`${pageType}.description`}
          label="Page description"
          mb={32}
          required
          validate={validateField}
        />
        <Flex gap={16} flexWrap="wrap">
          {pageVariables.map((variable) => (
            <Button
              key={variable}
              variant="secondary"
              label={`{{ ${variable} }}`}
              onClick={() => insertVariable(variable)}
            />
          ))}
        </Flex>
      </Flex>
      <PreviewSeo
        menuUrl={menuUrl}
        title={formValues?.[pageType]?.title ?? ''}
        description={formValues?.[pageType]?.description ?? ''}
        variables={seoVariables}
      />
    </Flex>
  );
};
